import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Divider,
} from "@material-ui/core";
import { Add, ArrowBack, Edit, Delete } from "@material-ui/icons";
import ExerciseSelector from "./ExerciseSelector";
import ExerciseList from "../views/ExerciseList";
import ExerciseForm from "../forms/ExerciseForm";
import ExerciseFilterHeader from "./ExerciseSelectorHeader";
import DataService from "services/DataService";
import exerciseConstants from "constants/Exercises/exerciseConstants";
import { EXERCISE_MODULES } from "../exerciseModule";
import { useExerciseFilter } from "../hooks/useExerciseFilter";

/**
 * Componente seletor de exercícios para o perfil de Administrador.
 *
 * Permite visualizar, criar, editar e excluir exercícios
 * de acordo com o tipo selecionado (Máquina ou Gramática).
 *
 * O componente encapsula:
 * - Listagem de exercícios
 * - Filtro por subtipo
 * - Formulário de criação/edição
 * - Ações administrativas (editar/excluir)
 *
 * @author Willian Brandao
 *
 * @param {Object} props Propriedades do componente.
 * @param {boolean} props.open Controla a abertura do diálogo.
 * @param {Function} props.onClose Função para fechar o diálogo.
 * @param {string} props.language Idioma atual ('pt' ou 'en').
 * @param {string} props.type Tipo de exercício ('MACHINE' ou 'GRAMMAR').
 * @param {Function} props.onSelect Callback chamado ao selecionar um exercício.
 * @param {Function} props.setAutomatonType Define o tipo de autômato no contexto global.
 *
 * @returns {JSX.Element|null} Componente renderizado ou null em caso de tipo inválido.
 */
export default function AdminExerciseSelector(props) {
  const [isEditing, setIsEditing] = useState(false);
  const [current, setCurrent] = useState(null);

  const labels = exerciseConstants.title;
  const buttonLabels = exerciseConstants.buttons;

  const module = EXERCISE_MODULES[props.type];

  if (!module) {
    console.error("Tipo inválido:", props.type);
    return null;
  }

  const subTypeLabel = labels[module.labelKey][props.language];

  /**
   * Realiza a criação ou atualização de um exercício.
   *
   * Se o payload possuir `id`, executa atualização,
   * caso contrário, realiza a criação de um novo exercício.
   *
   * @param {Object} payload Dados do exercício provenientes do formulário.
   * @param {Object} config Configurações de autenticação da requisição.
   * @param {Function} loadData Função responsável por recarregar a lista.
   *
   * @returns {Promise<void>}
   */
  const handleSave = async (payload, config, loadData) => {
    if (payload.id) {
      await DataService.updateExercise(payload.id, payload, config);
    } else {
      await DataService.saveExercise(payload, config);
    }
    setIsEditing(false);
    loadData();
  };

  /**
   * Remove um exercício existente.
   *
   * Após a exclusão, recarrega a lista de exercícios.
   *
   * @param {string} id Identificador único do exercício.
   * @param {Object} config Configurações de autenticação da requisição.
   * @param {Function} loadData Função responsável por recarregar a lista.
   *
   * @returns {Promise<void>}
   */
  const handleDelete = async (id, config, loadData) => {
    await DataService.deleteExercise(id, config);
    loadData();
  };

  return (
    <ExerciseSelector {...props} isAdmin={true}>
      {({ exercises, loadData, getAuthConfig }) => {
        /**
         * Aplica o hook de filtragem.
         */
        const { selectedType, setSelectedType, filteredExercises } =
          useExerciseFilter(exercises);

        const panelTitle = `${labels.admin[props.language]}${subTypeLabel}`;

        return (
          <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box display="flex" alignItems="center">
                  {isEditing && (
                    <IconButton
                      onClick={() => {
                        setIsEditing(false);
                      }}
                      size="small"
                      style={{ marginRight: 8 }}
                    >
                      <ArrowBack />
                    </IconButton>
                  )}
                  <Typography variant="h6">{panelTitle}</Typography>
                </Box>

                {!isEditing && (
                  <IconButton
                    color="primary"
                    onClick={() => {
                      setCurrent({});
                      setIsEditing(true);
                    }}
                  >
                    <Add />
                  </IconButton>
                )}
              </Box>
            </DialogTitle>

            <Divider />

            <DialogContent dividers>
              {isEditing ? (
                <ExerciseForm
                  current={current}
                  setCurrent={setCurrent}
                  type={props.type}
                  language={props.language}
                  onCancel={() => {
                    setIsEditing(false);
                  }}
                  onSave={(payload) => {
                    handleSave(payload, getAuthConfig(), loadData);
                  }}
                />
              ) : (
                <>
                  {/* Filtro centralizado exibido apenas na listagem */}
                  <ExerciseFilterHeader
                    type={props.type}
                    language={props.language}
                    selectedType={selectedType}
                    setSelectedType={setSelectedType}
                  />

                  <ExerciseList
                    exercises={filteredExercises}
                    language={props.language}
                    onSelect={(ex) => {
                      if (props.type === "MACHINE") {
                        if (ex.automatonType) {
                          props.setAutomatonType(ex.automatonType);
                        }
                      }
                      props.onSelect(ex.id);
                    }}
                    renderActions={(ex) => {
                      return (
                        <>
                          <IconButton
                            edge="end"
                            aria-label={buttonLabels.edit[props.language]}
                            onClick={() => {
                              setCurrent(ex);
                              setIsEditing(true);
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label={buttonLabels.delete[props.language]}
                            color="secondary"
                            onClick={() => {
                              handleDelete(ex.id, getAuthConfig(), loadData);
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </>
                      );
                    }}
                  />
                </>
              )}
            </DialogContent>
          </Dialog>
        );
      }}
    </ExerciseSelector>
  );
}
